import type { Server } from "@cloudserver/contracts";
import { useTranslation } from "react-i18next";
import { useRdpReachability } from "../api/rdp.js";
import { useProjectAccess } from "../api/users.js";
import { ReachabilityNote } from "../terminal/setup-card.js";
import { HelpLink } from "../ui/help.js";
import { Card, TextField } from "../ui/primitives.js";
import type { RdpCredentials } from "./connection.js";

/** The Windows sign-in being typed: the password lives only in this form's state. */
export type RdpForm = Required<RdpCredentials>;

/** Whether the form can be sent. */
export const rdpFormValid = (form: RdpForm): boolean =>
  form.username.trim().length > 0 && form.password.length > 0;

/** The form as credentials for `openRdp` / `connect` (an empty domain is left out). */
export const rdpCredentials = (form: RdpForm): RdpCredentials => ({
  username: form.username.trim(),
  password: form.password,
  ...(form.domain.trim() ? { domain: form.domain.trim() } : {}),
});

/** Windows user, password and domain, typed for one session: never stored or logged. */
export function RdpSignInFields({
  form,
  onChange,
}: {
  form: RdpForm;
  onChange: (form: RdpForm) => void;
}) {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col gap-3">
      <TextField
        label={t("rdp.signIn.user")}
        hint={t("rdp.signIn.userHint")}
        value={form.username}
        onChange={(event) => onChange({ ...form, username: event.target.value })}
        autoCapitalize="off"
        autoComplete="off"
        spellCheck={false}
      />
      <TextField
        label={t("rdp.signIn.password")}
        hint={t("rdp.signIn.passwordHint")}
        type="password"
        autoComplete="off"
        value={form.password}
        onChange={(event) => onChange({ ...form, password: event.target.value })}
      />
      <TextField
        label={t("rdp.signIn.domain")}
        hint={t("rdp.signIn.domainHint")}
        value={form.domain}
        onChange={(event) => onChange({ ...form, domain: event.target.value })}
        autoCapitalize="off"
        autoComplete="off"
        spellCheck={false}
      />
    </div>
  );
}

/** Port 3389 through the server's firewalls, or that this installation has no gateway. */
export function RdpReachabilityNote({
  projectId,
  serverId,
}: {
  projectId: string;
  serverId: string;
}) {
  const { t } = useTranslation();
  const reach = useRdpReachability(projectId, serverId);
  if (!reach.data) return null;
  if (!reach.data.gateway) {
    return (
      <p role="status" className="text-sm text-warn">
        {t("rdp.unavailable")}
      </p>
    );
  }
  return <ReachabilityNote projectId={projectId} reach={reach.data} kind="rdp" />;
}

/**
 * "Remote desktop" on a Windows server's overview: how it works and
 * whether port 3389 is reachable. Not for guests.
 */
export function RdpSetupCard({ projectId, server }: { projectId: string; server: Server }) {
  const { t } = useTranslation();
  const access = useProjectAccess(projectId);
  if (access !== "owner" && access !== "admin") return null;
  return (
    <Card className="flex flex-col gap-4 lg:col-span-2">
      <div className="flex flex-col gap-1">
        <h2 className="text-base font-semibold">{t("rdp.setup.title")}</h2>
        <p className="text-sm text-muted">{t("rdp.setup.intro")}</p>
      </div>
      <RdpReachabilityNote projectId={projectId} serverId={server.id} />
      <HelpLink topic="remote-desktop" />
    </Card>
  );
}
